import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, MessageSquare, Send, Trash2, User, Calendar, CheckCircle } from 'lucide-react'
import { useUser } from '../contexts/UserContext'
import { getFeedback, respondToFeedback, deleteFeedback, Feedback } from '../api/feedback'

const AdminFeedbackPage = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useUser()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<Feedback[]>([])
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null)
  const [responseText, setResponseText] = useState('')
  const [responding, setResponding] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)

  const canAccess = isAuthenticated && user?.userType === 'ADMIN'

  const loadFeedback = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getFeedback()
      setFeedback(data)
    } catch (e: any) {
      setError(e?.message || 'Failed to load feedback')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    if (user?.userType !== 'ADMIN') {
      navigate('/')
      return
    }
    loadFeedback()
  }, [isAuthenticated, user, navigate])

  const handleRespond = async () => {
    if (!selectedFeedback || !responseText.trim()) return

    try {
      setResponding(true)
      const updated = await respondToFeedback(selectedFeedback.id, responseText)
      setFeedback(feedback.map(f => f.id === updated.id ? updated : f))
      setSelectedFeedback(updated)
      setResponseText('')
      alert('Response sent successfully!')
    } catch (e: any) {
      alert(e?.message || 'Failed to send response')
    } finally {
      setResponding(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this feedback?')) return

    try {
      setDeleting(id)
      await deleteFeedback(id)
      setFeedback(feedback.filter(f => f.id !== id))
      if (selectedFeedback?.id === id) {
        setSelectedFeedback(null)
        setResponseText('')
      }
    } catch (e: any) {
      alert(e?.message || 'Failed to delete feedback')
    } finally {
      setDeleting(null)
    }
  }

  const formatDate = (date: Date | string | null) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const unrespondedCount = feedback.filter(f => !f.response).length

  if (!canAccess) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Feedback Management</h1>
          <p className="text-gray-600">
            View and respond to feedback from users
            {unrespondedCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                {unrespondedCount} unresponded
              </span>
            )}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading feedback...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Feedback List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    All Feedback ({feedback.length})
                  </h2>
                </div>
                <div className="divide-y divide-gray-200 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {feedback.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No feedback yet
                    </div>
                  ) : (
                    feedback.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setSelectedFeedback(item)
                          setResponseText('')
                        }}
                        className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                          selectedFeedback?.id === item.id ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                        } ${!item.response ? 'bg-yellow-50' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{item.name}</div>
                            <div className="text-sm text-gray-600">{item.email}</div>
                          </div>
                          {!item.response && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              New
                            </span>
                          )}
                          {item.response && (
                            <CheckCircle className="h-5 w-5 text-green-500 ml-2" />
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mb-1">
                          {formatDate(item.createdAt)}
                        </div>
                        {item.userType && (
                          <div className="text-xs text-gray-500 mb-1">
                            Type: {item.userType.replace('_', ' ')}
                          </div>
                        )}
                        <p className="text-sm text-gray-700 line-clamp-2 mt-2">
                          {item.message}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Feedback Detail & Response */}
            <div className="lg:col-span-2">
              {selectedFeedback ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">
                          {selectedFeedback.name}
                        </h2>
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2" />
                            {selectedFeedback.email}
                          </div>
                          {selectedFeedback.userType && (
                            <div className="flex items-center">
                              <User className="h-4 w-4 mr-2" />
                              {selectedFeedback.userType.replace('_', ' ')}
                            </div>
                          )}
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2" />
                            {formatDate(selectedFeedback.createdAt)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(selectedFeedback.id)}
                        disabled={deleting === selectedFeedback.id}
                        className="text-red-600 hover:text-red-700 disabled:opacity-50"
                        title="Delete feedback"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Message:</h3>
                      <p className="text-gray-900 whitespace-pre-wrap">{selectedFeedback.message}</p>
                    </div>

                    {selectedFeedback.response ? (
                      <div className="mb-6">
                        <div className="flex items-center mb-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                          <h3 className="text-sm font-medium text-gray-700">Your Response:</h3>
                          {selectedFeedback.respondedBy && (
                            <span className="ml-2 text-xs text-gray-500">
                              by {selectedFeedback.respondedBy.firstName} {selectedFeedback.respondedBy.lastName}
                            </span>
                          )}
                          <span className="ml-auto text-xs text-gray-500">
                            {formatDate(selectedFeedback.respondedAt)}
                          </span>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <p className="text-gray-900 whitespace-pre-wrap">{selectedFeedback.response}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-6">
                        <label htmlFor="response" className="block text-sm font-medium text-gray-700 mb-2">
                          <MessageSquare className="h-4 w-4 inline mr-1" />
                          Write a Response:
                        </label>
                        <textarea
                          id="response"
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Type your response here..."
                        />
                        <button
                          onClick={handleRespond}
                          disabled={!responseText.trim() || responding}
                          className="mt-3 w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
                        >
                          {responding ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Send Response
                            </>
                          )}
                        </button>
                        <p className="mt-2 text-xs text-gray-500">
                          The user will receive an email with your response.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Select a feedback item to view details and respond</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminFeedbackPage

